import React from "react"

// every internal link starts with $url. and closed with $
// every internal link has to contain at least one word: $url.parameter$
// an internal link might contain a second word: $url.workspace.create$
// an internal link might contain one or more number after one or more words:
// $url.item.456$
// $url.report.execute.456$
// $url.smartlist.456.123$
// $url.actionbutton.concept.456.123$
// an internal link can contain a label, which will become the name text content of the link
// $url.item.456#This is the content for the link$

const internalLinksRegex = /(\$url.(\w)+(.\w+)*(.\d+)*(#[^$\n]+)?\$)/g
// Explanation for this regex can be found here: https://daringfireball.net/2010/07/improved_regex_for_matching_urls
const externalLinksRegex =
    /\b((?:(ftp|http|https):(\/\/|\\\\)|www\d{0,3}[.]|[a-z0-9.-]+[.][a-z]{2,4}\/)(?:[^\s<>]+)(?:[^\s`![\]{};:'".,<>?«»“”‘’]))/gm

export const urlify = (
    text: string,
    handleInternalLinks?: (link: string, index: number) => JSX.Element,
): (string | JSX.Element)[] => {
    let urlifiedElements = parseTextToJSX(
        text,
        externalLinksRegex,
        (match: string, index: number) => {
            // if the url starts with www. we prefix with http
            // this way both http and https websites will work
            // as that website should redirect from http to https if it's available
            const prefix = match.startsWith("www.") ? "http://" : ""
            const href = `${prefix}${match}`
            return (
                <a
                    data-ft-link-type="external"
                    data-ft-link={match}
                    href={href}
                    // since we're parsing the web links first, then the internal,
                    // there is no way pass an index based on their joint order
                    key={`external-${index}`}
                    rel="noreferrer"
                    target="_blank"
                    title={match}
                >
                    {match}
                </a>
            )
        },
    )

    // we should only run the second regex on the string parts
    if (!!handleInternalLinks) {
        urlifiedElements = urlifiedElements.flatMap((element) =>
            typeof element === "string"
                ? parseTextToJSX(
                      element,
                      internalLinksRegex,
                      handleInternalLinks,
                  )
                : element,
        )
    }

    return urlifiedElements
}

const parseTextToJSX = (
    text: string,
    regex: RegExp,
    converter: (match: string, key: number) => JSX.Element,
): (JSX.Element | string)[] => {
    const parts: (JSX.Element | string)[] = []

    const matches = text.match(regex)
    matches?.forEach((match, index) => {
        const split = text.split(match)

        // Transform the matched string into an actual JSX element here
        const newValueForMatch = converter(match, index)

        // Anything before the match can be added as JSX string.
        parts.push(split.shift() ?? "")

        // We add the matched string
        parts.push(newValueForMatch)

        // We continue with whatever's left.
        // make sure we add the rest back
        // the join prevents from losing the content if the match is there more times. Example:
        // "www.google.com and http://www.google.com"
        // because there is a repeating substring, this can cause issues
        text = split.join(match)
    })
    parts.push(text)

    return parts
}
